import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from 'components/shadcn/button';
import { ScrollArea } from 'components/shadcn/scroll-area';
import { useRTL } from '@/hooks';
import { ragApi } from '@/api/rag';
import { SourceEditor } from '@/components/rag/SourceEditor';
import { RagSettings } from '@/components/rag/RagSettings';

export const EditChatRag = () => {
  const { id: chatId } = useParams();
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const queryClient = useQueryClient();

  // We sort sources by ID to keep order stable
  const { data: sources, isLoading } = useQuery({
    queryKey: ['rag-sources', chatId],
    queryFn: async () => {
      const data = await ragApi.getSources(chatId!);
      return data.sort((a, b) => a.id - b.id);
    },
    enabled: !!chatId,
  });

  const addSourceMutation = useMutation({
    mutationFn: () => ragApi.createSource(chatId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rag-sources'] }),
    onError: () => toast.error(t('common.error')),
  });

  if (!chatId) return null;

  return (
    <div className="flex flex-col h-full gap-4 px-8 pb-8" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-row justify-between items-center gap-3 pt-6">
        <div>
          <h1 className="text-xl text-balance">{t('dashboard.chatForm.dialogs.ragTitle')}</h1>
          <div className="text-sm text-muted-foreground mt-1 text-pretty">
            {t('dashboard.chatForm.dialogs.ragSubtitle')}
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['rag-sources'] })}
            disabled={isLoading}
          >
            {t('ui.refresh')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => addSourceMutation.mutate()}
            disabled={addSourceMutation.isPending}
          >
            {t('dashboard.chatForm.rag.addSource')}
          </Button>
        </div>
      </div>

      <RagSettings chatId={chatId} />

      <ScrollArea className="flex-1 -mr-4 pr-4">
        <div className="space-y-4 pb-4">
          {!sources?.length && !isLoading && (
            <div className="text-center text-muted-foreground p-8 border rounded-md border-dashed">
              {t('dashboard.chatForm.rag.empty')}
            </div>
          )}
          {sources?.map((source) => <SourceEditor key={source.id} source={source} />)}
        </div>
      </ScrollArea>
    </div>
  );
};
